import type {
  GenerationAspectRatio,
  GenerationBackground,
  GenerationImageSize,
  GenerationModel,
  GenerationQuality,
  GenerationResolution,
} from '@/ai/models';
import {
  ALL_MODELS,
  ASPECT_RATIO_DISPLAY,
  BACKGROUND_DISPLAY,
  IMAGE_SIZE_DISPLAY,
  QUALITY_DISPLAY,
  RESOLUTION_DISPLAY,
} from '@/ai/models';
import { FREE_IMAGE_TRIAL_WELCOME } from '@/lib/free-image-trial';
import type { GenerationContinuationType } from '@/db/app.schema';
import {
  VideoAspectRatio,
  type VideoGenerationMode,
  VideoGenerationModel,
  VideoResolution,
} from '@/ai/video-models';
import {
  VIDEO_REFERENCE_MODES,
  VIDEO_REFERENCE_TEMPLATES,
  normalizeVideoReferenceRoles,
  serializeVideoReferenceRoles,
  type VideoReferenceMode,
  type VideoReferenceTemplate,
} from '@/lib/video-reference-roles';

export interface ImageEditorRouteSearch {
  aspectRatio?: GenerationAspectRatio;
  background?: GenerationBackground;
  promptId?: number;
  creationId?: string;
  imageSize?: GenerationImageSize;
  model?: GenerationModel;
  prompt?: string;
  referenceToken?: string;
  quality?: GenerationQuality;
  resolution?: GenerationResolution;
  welcome?: typeof FREE_IMAGE_TRIAL_WELCOME;
  parentGenerationId?: string;
  continuationType?: GenerationContinuationType;
  sourceVideoId?: string;
  videoPrompt?: string;
  videoModel?: VideoGenerationModel;
  videoAspectRatio?: VideoAspectRatio;
  videoDuration?: number;
  videoResolution?: VideoResolution;
  videoMode?: VideoGenerationMode;
  videoGenerateAudio?: '1';
  videoReferenceMode?: VideoReferenceMode;
  videoReferenceTemplate?: VideoReferenceTemplate;
  videoReferenceRoles?: string;
  videoEditReferenceIndex?: number;
  videoWorkflow?: 'image-to-video' | 'reference-to-video';
}

const IMAGE_ASPECT_RATIOS = new Set<string>(Object.keys(ASPECT_RATIO_DISPLAY));
const IMAGE_BACKGROUNDS = new Set<string>(Object.keys(BACKGROUND_DISPLAY));
const IMAGE_MODELS = new Set<string>(ALL_MODELS.map((model) => model.id));
const IMAGE_QUALITIES = new Set<string>(Object.keys(QUALITY_DISPLAY));
const IMAGE_RESOLUTIONS = new Set<string>(Object.keys(RESOLUTION_DISPLAY));
const IMAGE_SIZES = new Set<string>(Object.keys(IMAGE_SIZE_DISPLAY));
const VIDEO_MODELS = new Set<string>(Object.values(VideoGenerationModel));
const VIDEO_ASPECT_RATIOS = new Set<string>(Object.values(VideoAspectRatio));
const VIDEO_RESOLUTIONS = new Set<string>(Object.values(VideoResolution));
const VIDEO_MODES = new Set<string>(['normal', 'fun', 'spicy']);
const VIDEO_REFERENCE_MODE_VALUES = new Set<string>(VIDEO_REFERENCE_MODES);
const VIDEO_REFERENCE_TEMPLATE_VALUES = new Set<string>(
  VIDEO_REFERENCE_TEMPLATES
);
const VIDEO_RETURN_WORKFLOWS = new Set([
  'image-to-video',
  'reference-to-video',
]);

export function validateImageEditorSearch(
  search: Record<string, unknown>
): ImageEditorRouteSearch {
  const result: ImageEditorRouteSearch = {};
  const aspectRatio = toEnum(
    search.aspectRatio,
    IMAGE_ASPECT_RATIOS
  ) as GenerationAspectRatio;
  const background = toEnum(
    search.background,
    IMAGE_BACKGROUNDS
  ) as GenerationBackground;
  const promptId = toPositiveInteger(search.promptId);
  const creationId = toOptionalString(search.creationId);
  const imageSize = toEnum(
    search.imageSize,
    IMAGE_SIZES
  ) as GenerationImageSize;
  const model = toEnum(search.model, IMAGE_MODELS) as GenerationModel;
  const prompt = toOptionalString(search.prompt);
  const referenceToken = toOptionalString(search.referenceToken);
  const quality = toEnum(search.quality, IMAGE_QUALITIES) as GenerationQuality;
  const resolution = toEnum(
    search.resolution,
    IMAGE_RESOLUTIONS
  ) as GenerationResolution;
  const welcome =
    search.welcome === FREE_IMAGE_TRIAL_WELCOME
      ? FREE_IMAGE_TRIAL_WELCOME
      : undefined;
  const parentGenerationId = toOptionalString(search.parentGenerationId);
  const continuationType = toContinuationType(search.continuationType);
  const sourceVideoId = toOptionalString(search.sourceVideoId);
  const videoPrompt = toOptionalString(search.videoPrompt);
  const videoModel = toEnum(
    search.videoModel,
    VIDEO_MODELS
  ) as VideoGenerationModel;
  const videoAspectRatio = toEnum(
    search.videoAspectRatio,
    VIDEO_ASPECT_RATIOS
  ) as VideoAspectRatio;
  const videoDuration = toPositiveInteger(search.videoDuration);
  const videoResolution = toEnum(
    search.videoResolution,
    VIDEO_RESOLUTIONS
  ) as VideoResolution;
  const videoMode = toEnum(
    search.videoMode,
    VIDEO_MODES
  ) as VideoGenerationMode;
  const videoReferenceMode = toEnum(
    search.videoReferenceMode,
    VIDEO_REFERENCE_MODE_VALUES
  ) as VideoReferenceMode;
  const videoReferenceTemplate = toEnum(
    search.videoReferenceTemplate,
    VIDEO_REFERENCE_TEMPLATE_VALUES
  ) as VideoReferenceTemplate;
  const videoReferenceRoles = normalizeVideoReferenceRoles(
    search.videoReferenceRoles
  );
  const videoEditReferenceIndex = toNonNegativeInteger(
    search.videoEditReferenceIndex
  );
  const videoWorkflow = toEnum(search.videoWorkflow, VIDEO_RETURN_WORKFLOWS) as
    | 'image-to-video'
    | 'reference-to-video';

  if (aspectRatio) result.aspectRatio = aspectRatio;
  if (background) result.background = background;
  if (promptId) result.promptId = promptId;
  if (creationId) result.creationId = creationId;
  if (imageSize) result.imageSize = imageSize;
  if (model) result.model = model;
  if (prompt) result.prompt = prompt;
  if (referenceToken) result.referenceToken = referenceToken;
  if (quality) result.quality = quality;
  if (resolution) result.resolution = resolution;
  if (welcome) result.welcome = welcome;
  if (parentGenerationId && continuationType) {
    result.parentGenerationId = parentGenerationId;
    result.continuationType = continuationType;
  }
  if (sourceVideoId) result.sourceVideoId = sourceVideoId;
  if (videoPrompt) result.videoPrompt = videoPrompt;
  if (videoModel) result.videoModel = videoModel;
  if (videoAspectRatio) result.videoAspectRatio = videoAspectRatio;
  if (videoDuration) result.videoDuration = videoDuration;
  if (videoResolution) result.videoResolution = videoResolution;
  if (videoMode) result.videoMode = videoMode;
  if (search.videoGenerateAudio === '1') result.videoGenerateAudio = '1';
  if (videoReferenceMode) result.videoReferenceMode = videoReferenceMode;
  if (videoReferenceTemplate) {
    result.videoReferenceTemplate = videoReferenceTemplate;
  }
  if (videoReferenceRoles.length > 0) {
    result.videoReferenceRoles =
      serializeVideoReferenceRoles(videoReferenceRoles);
  }
  if (videoEditReferenceIndex !== undefined) {
    result.videoEditReferenceIndex = videoEditReferenceIndex;
  }
  if (videoWorkflow) result.videoWorkflow = videoWorkflow;

  return result;
}

function toContinuationType(value: unknown) {
  return value === 'continue' || value === 'variation' || value === 'retry'
    ? value
    : undefined;
}

function toOptionalString(value: unknown) {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function toPositiveInteger(value: unknown) {
  if (value === undefined || value === null || value === '') return undefined;
  const numberValue = Number(value);
  if (!Number.isInteger(numberValue) || numberValue <= 0) return undefined;
  return numberValue;
}

function toNonNegativeInteger(value: unknown) {
  if (value === undefined || value === null || value === '') return undefined;
  const numberValue = Number(value);
  if (!Number.isInteger(numberValue) || numberValue < 0) return undefined;
  return numberValue;
}

function toEnum(value: unknown, allowed: Set<string>) {
  return typeof value === 'string' && allowed.has(value) ? value : undefined;
}
